import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

export interface CapturedPhoto {
  file: File;
  dataUrl: string;
}

@Component({
  selector: 'app-camera-capture',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './camera-capture.html',
})
export class CameraCaptureComponent implements OnChanges, OnDestroy {

  // ── Inputs / Outputs ─────────────────────────────────────────
  @Input() isOpen = false;
  @Output() photoTaken  = new EventEmitter<CapturedPhoto>();
  @Output() cancelled   = new EventEmitter<void>();

  // ── View refs ─────────────────────────────────────────────────
  @ViewChild('videoEl')  videoEl!:  ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('flashEl')  flashEl!:  ElementRef<HTMLDivElement>;

  // ── Stream state ─────────────────────────────────────────────
  streamActive  = false;
  capturedImage: string | null = null;
  cameraError:   string | null = null;
  isSwitching    = false;  // shows a brief spinner while switching

  // ── Camera enumeration ────────────────────────────────────────
  availableCameras:  MediaDeviceInfo[] = [];
  selectedCameraId:  string | null     = null;
  hasMultipleCameras = false;

  /**
   * The ACTIVE facing mode of the CURRENTLY RUNNING stream.
   * This is the ground truth used for:
   *   - CSS mirroring the live video preview
   *   - Deciding whether to flip the canvas on capture
   *
   * Default: 'user' (front camera).  Toggled by switchCamera().
   */
  activeFacingMode: 'user' | 'environment' = 'user';

  // ── Flash / torch ─────────────────────────────────────────────
  flashMode:  'off' | 'on' = 'off';
  private torchTrack: MediaStreamTrack | null = null;

  // ── Zoom ─────────────────────────────────────────────────────
  currentZoom = 1;
  private readonly ZOOM_STEPS = [1, 1.5, 2, 3];
  private zoomIndex = 0;

  private activeStream: MediaStream | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  // ════════════════════════════════════════════════════════════
  //  LIFECYCLE
  // ════════════════════════════════════════════════════════════

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.reset();
        setTimeout(() => this.requestCamera(), 80);
      } else {
        this.stopStream();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopStream();
  }

  // ════════════════════════════════════════════════════════════
  //  CAMERA INITIALISATION
  // ════════════════════════════════════════════════════════════

  async requestCamera(): Promise<void> {
    this.cameraError  = null;
    this.streamActive = false;
    this.cdr.detectChanges();

    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'Your browser does not support camera access. Please try Chrome, Firefox, or Safari.';
      this.cdr.detectChanges();
      return;
    }

    try {
      // First pass enumeration (labels may be empty before permission)
      await this.enumerateCameras();

      const constraints = this.buildConstraints();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      this.activeStream = stream;
      this.torchTrack   = stream.getVideoTracks()[0] ?? null;

      // ── Resolve the ACTUAL facing mode from the live track ────
      // This is the reliable source of truth — don't rely on what
      // we requested; the browser may have picked a different camera.
      this.activeFacingMode = this.resolveFacingModeFromTrack(
        this.torchTrack,
        this.activeFacingMode,
      );

      // Attach to video element
      const video = this.videoEl?.nativeElement;
      if (!video) return;
      video.srcObject = stream;
      await video.play();

      this.streamActive = true;
      this.isSwitching  = false;
      this.currentZoom  = 1;
      this.zoomIndex    = 0;

      // Re-enumerate after permission granted — labels now available
      await this.enumerateCameras();
      this.cdr.detectChanges();
    } catch (err: any) {
      this.streamActive = false;
      this.isSwitching  = false;
      this.cameraError  = this.translateCameraError(err);
      this.cdr.detectChanges();
    }
  }

  // ════════════════════════════════════════════════════════════
  //  FACING MODE — ground-truth resolver
  //
  //  Priority order:
  //  1. Track settings  → track.getSettings().facingMode  (most reliable on mobile)
  //  2. Device label    → contains 'front'/'back'/etc.
  //  3. Fallback        → whatever we last requested
  // ════════════════════════════════════════════════════════════

  private resolveFacingModeFromTrack(
    track:    MediaStreamTrack | null,
    fallback: 'user' | 'environment',
  ): 'user' | 'environment' {
    if (!track) return fallback;

    // 1. Try track settings (works on Chrome/Safari mobile)
    try {
      const settings = track.getSettings() as any;
      if (settings?.facingMode === 'user')        return 'user';
      if (settings?.facingMode === 'environment') return 'environment';
    } catch { /* getSettings not supported */ }

    // 2. Try device label
    const label = (track.label ?? '').toLowerCase();
    if (label.includes('front') || label.includes('selfie') || label.includes('user')) return 'user';
    if (label.includes('back')  || label.includes('rear')   || label.includes('environment')) return 'environment';

    // 3. Try matching the device in our enumerated list
    const deviceId = (track.getSettings?.() as any)?.deviceId;
    if (deviceId) {
      const device = this.availableCameras.find(c => c.deviceId === deviceId);
      if (device) {
        const dl = device.label.toLowerCase();
        if (dl.includes('front') || dl.includes('selfie') || dl.includes('user')) return 'user';
        if (dl.includes('back')  || dl.includes('rear')   || dl.includes('environment')) return 'environment';
      }
    }

    return fallback;
  }

  // ── Is the live stream currently front-facing? ───────────────
  get isFrontCamera(): boolean {
    return this.activeFacingMode === 'user';
  }

  private buildConstraints(): MediaStreamConstraints {
    const video: MediaTrackConstraints = {
      width:  { ideal: 1920 },
      height: { ideal: 1080 },
    };

    if (this.selectedCameraId) {
      // Exact device requested (user tapped a specific camera)
      video.deviceId = { exact: this.selectedCameraId };
    } else {
      // Let browser pick the best camera for the requested facing mode
      video.facingMode = { ideal: this.activeFacingMode };
    }

    return { video, audio: false };
  }

  private async enumerateCameras(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableCameras  = devices.filter(d => d.kind === 'videoinput');
      this.hasMultipleCameras = this.availableCameras.length > 1;

      // Set a default selectedCameraId only on first call (no camera chosen yet)
      if (!this.selectedCameraId && this.availableCameras.length > 0) {
        const front = this.availableCameras.find(c => {
          const l = c.label.toLowerCase();
          return l.includes('front') || l.includes('selfie') || l.includes('user');
        });
        this.selectedCameraId = (front ?? this.availableCameras[0]).deviceId;
      }
    } catch { /* enumeration failed — not critical */ }
  }

  private translateCameraError(err: any): string {
    const name = err?.name ?? '';
    if (name === 'NotAllowedError'    || name === 'PermissionDeniedError')
      return 'Camera permission was denied. Please allow camera access in your browser settings and try again.';
    if (name === 'NotFoundError'      || name === 'DevicesNotFoundError')
      return 'No camera was found on this device.';
    if (name === 'NotReadableError'   || name === 'TrackStartError')
      return 'Camera is in use by another app. Please close other apps and try again.';
    if (name === 'OverconstrainedError')
      return 'The selected camera does not support the required settings. Please try a different camera.';
    if (name === 'SecurityError')
      return 'Camera access requires a secure connection (HTTPS).';
    return 'Could not access camera. Please check your device settings and try again.';
  }

  // ════════════════════════════════════════════════════════════
  //  CAMERA SWITCH
  //
  //  Works on BOTH desktop (multiple cameras) and mobile (front/back).
  //  The switch button is always shown when a stream is active so
  //  mobile users on tablets/phones can always flip the camera.
  // ════════════════════════════════════════════════════════════

  async switchCamera(): Promise<void> {
    if (this.isSwitching) return;
    this.isSwitching = true;
    this.cdr.detectChanges();

    // Toggle the desired facing mode
    const nextFacing: 'user' | 'environment' =
      this.activeFacingMode === 'environment' ? 'user' : 'environment';

    // Try to find a matching camera in our enumerated list
    const match = this.availableCameras.find(c => {
      const l = c.label.toLowerCase();
      if (nextFacing === 'user')        return l.includes('front') || l.includes('selfie') || l.includes('user');
      if (nextFacing === 'environment') return l.includes('back')  || l.includes('rear')   || l.includes('environment');
      return false;
    });

    // Update desired state BEFORE stopping stream so buildConstraints uses it
    this.activeFacingMode = nextFacing;
    this.selectedCameraId = match?.deviceId ?? null; // null = let browser pick by facingMode

    this.stopStream();
    await this.requestCamera();
  }

  async selectCamera(deviceId: string): Promise<void> {
    if (deviceId === this.selectedCameraId || this.isSwitching) return;
    this.isSwitching      = true;
    this.selectedCameraId = deviceId;
    // Derive facing mode from label so mirroring is correct after selection
    const device = this.availableCameras.find(c => c.deviceId === deviceId);
    if (device) {
      const l = device.label.toLowerCase();
      if (l.includes('front') || l.includes('selfie') || l.includes('user')) {
        this.activeFacingMode = 'user';
      } else if (l.includes('back') || l.includes('rear') || l.includes('environment')) {
        this.activeFacingMode = 'environment';
      }
      // If label is empty the track resolver will sort it out after start
    }
    this.stopStream();
    await this.requestCamera();
  }

  // ════════════════════════════════════════════════════════════
  //  FLASH / TORCH
  // ════════════════════════════════════════════════════════════

  async toggleFlash(): Promise<void> {
    if (!this.torchTrack) return;
    const next = this.flashMode === 'off' ? 'on' : 'off';
    try {
      const caps = this.torchTrack.getCapabilities?.() as any;
      if (caps?.torch) {
        await (this.torchTrack as any).applyConstraints({ advanced: [{ torch: next === 'on' }] });
      }
    } catch { /* torch not supported — visual flash still fires on capture */ }
    this.flashMode = next;
    this.cdr.detectChanges();
  }

  // ════════════════════════════════════════════════════════════
  //  ZOOM
  // ════════════════════════════════════════════════════════════

  cycleZoom(): void {
    this.zoomIndex  = (this.zoomIndex + 1) % this.ZOOM_STEPS.length;
    this.currentZoom = this.ZOOM_STEPS[this.zoomIndex];
    this.applyZoom();
  }

  private applyZoom(): void {
    if (!this.torchTrack) return;
    try {
      const caps = this.torchTrack.getCapabilities?.() as any;
      if (caps?.zoom) {
        const clamped = Math.min(
          Math.max(this.currentZoom, caps.zoom.min ?? 1),
          caps.zoom.max ?? 10,
        );
        (this.torchTrack as any).applyConstraints({ advanced: [{ zoom: clamped }] });
        return;
      }
    } catch { /* zoom constraint not supported */ }

    // CSS scale fallback
    const video = this.videoEl?.nativeElement;
    if (video) {
      // Combine zoom scale with the existing mirror transform
      const mirror = this.isFrontCamera ? 'scaleX(-1)' : '';
      video.style.transform = this.currentZoom > 1
        ? `scale(${this.currentZoom}) ${mirror}`.trim()
        : mirror || '';
    }
  }

  // ════════════════════════════════════════════════════════════
  //  CAPTURE
  //
  //  Mirror rules:
  //  ┌──────────────────┬───────────────────┬────────────────────┐
  //  │  Camera type     │  Live preview CSS  │  Captured canvas   │
  //  ├──────────────────┼───────────────────┼────────────────────┤
  //  │  Back/env camera │  NO mirror         │  NO flip           │
  //  │  Front camera    │  CSS scaleX(-1)    │  NO flip (undo CSS)│
  //  └──────────────────┴───────────────────┴────────────────────┘
  //
  //  The live preview is mirrored for front-cam (natural selfie feel).
  //  The CAPTURED image is NOT flipped — the canvas draws the raw
  //  video pixels which are already the correct real-world orientation.
  //  This matches the behaviour of every native camera app:
  //    • Preview: mirrored  →  raising your RIGHT hand shows on the RIGHT
  //    • Photo saved: NOT mirrored → the world is recorded as-is
  // ════════════════════════════════════════════════════════════

  capturePhoto(): void {
    if (!this.streamActive) return;

    const video  = this.videoEl?.nativeElement;
    const canvas = this.canvasEl?.nativeElement;
    if (!video || !canvas) return;

    canvas.width  = video.videoWidth  || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.triggerShutterFlash();

    // Draw the raw video frame — no canvas flip needed.
    // The video element shows mirrored (via CSS) but the underlying
    // pixel data is always the real-world orientation.
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    this.capturedImage = canvas.toDataURL('image/jpeg', 0.92);
    this.cdr.detectChanges();
  }

  private triggerShutterFlash(): void {
    const flash = this.flashEl?.nativeElement;
    if (!flash) return;
    flash.style.opacity    = this.flashMode === 'on' ? '0.95' : '0.5';
    flash.style.transition = 'opacity 0ms';
    setTimeout(() => {
      flash.style.transition = 'opacity 200ms ease-out';
      flash.style.opacity    = '0';
    }, 60);
  }

  retakePhoto(): void {
    this.capturedImage = null;
    this.cdr.detectChanges();
  }

  // ════════════════════════════════════════════════════════════
  //  USE / CANCEL
  // ════════════════════════════════════════════════════════════

  usePhoto(): void {
    if (!this.capturedImage) return;
    const file = this.dataUrlToFile(this.capturedImage, `capture-${Date.now()}.jpg`);
    this.photoTaken.emit({ file, dataUrl: this.capturedImage });
    this.stopStream();
  }

  onCancel(): void {
    this.stopStream();
    this.cancelled.emit();
  }

  // ════════════════════════════════════════════════════════════
  //  HELPERS
  // ════════════════════════════════════════════════════════════

  private dataUrlToFile(dataUrl: string, filename: string): File {
    const arr   = dataUrl.split(',');
    const mime  = arr[0].match(/:(.*?);/)![1];
    const bstr  = atob(arr[1]);
    let n       = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  private stopStream(): void {
    if (this.activeStream) {
      this.activeStream.getTracks().forEach(t => t.stop());
      this.activeStream = null;
    }
    const video = this.videoEl?.nativeElement;
    if (video) video.srcObject = null;
    this.torchTrack   = null;
    this.streamActive = false;
    this.flashMode    = 'off';
    this.cdr.detectChanges();
  }

  private reset(): void {
    this.capturedImage    = null;
    this.cameraError      = null;
    this.streamActive     = false;
    this.isSwitching      = false;
    this.currentZoom      = 1;
    this.zoomIndex        = 0;
    this.flashMode        = 'off';
    this.activeFacingMode = 'user'; // start with front camera by default
    this.selectedCameraId = null;
  }
}